// Owns its own mutation (the only child that does) because the 409-conflict UX
// renders a contextual error link inline next to the input; lifting that state
// to the orchestrator would force every sibling to know about website edit failures.
import { CircleCheck, ExternalLink, Pencil, Plus, X } from "lucide-mui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { keys } from "../../../api/queryKeys.ts";
import { api, ApiError } from "../../../api/client.ts";
import type { MasterCompanyDetail } from "../../../api/client.ts";
import FormattedDate from "../../../components/FormattedDate.tsx";
import { useTenant } from "../../../contexts/TenantContext.tsx";
import { ensureHttps } from "../../../utils/url.ts";

interface EditableWebsiteRowProps {
  company: MasterCompanyDetail;
}

export default function EditableWebsiteRow({ company }: EditableWebsiteRowProps) {
  const queryClient = useQueryClient();
  const { tenantPath } = useTenant();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [error, setError] = useState<{ message: string; conflictId?: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (domain: string | null) => api.updateMasterCompany(company.id, { domain }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.masterCompany.detail(company.id), data);
      setEditing(false);
      setError(null);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: { message?: string; company_id?: string } } | undefined;
        setError({
          message: body?.error?.message ?? "Domain already used",
          conflictId: body?.error?.company_id,
        });
      } else {
        setError({ message: err instanceof Error ? err.message : "Failed to save" });
      }
    },
    meta: { suppressErrorToast: true },
  });

  const startEdit = () => {
    setValue(company.website ?? company.domain ?? "");
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const save = () => {
    const trimmed = value.trim();
    mutation.mutate(trimmed === "" ? null : trimmed);
  };

  const websiteUrl = ensureHttps(company.website) ?? ensureHttps(company.domain);
  const displayText = company.domain ?? company.website ?? "";
  const updatedAt = company.field_updated_at?.website ?? company.field_updated_at?.domain;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Website
      </Typography>
      {editing ? (
        <Box>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mt: 0.25 }}>
            <TextField
              size="small"
              autoFocus
              fullWidth
              value={value}
              placeholder="example.com or https://example.com"
              disabled={mutation.isPending}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancel();
                }
              }}
              sx={{ maxWidth: 360 }}
            />
            <Tooltip title="Save (Enter)">
              <span>
                <IconButton size="small" onClick={save} loading={mutation.isPending}>
                  <CircleCheck fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Cancel (Esc)">
              <span>
                <IconButton size="small" onClick={cancel} disabled={mutation.isPending}>
                  <X fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          {error && (
            <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
              {error.message}
              {error.conflictId && (
                <>
                  {" "}
                  <Link
                    component={RouterLink}
                    to={tenantPath(`/companies/${error.conflictId}`)}
                    target="_blank"
                  >
                    View company
                  </Link>
                </>
              )}
            </Typography>
          )}
        </Box>
      ) : websiteUrl ? (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" component="span">
              <Link
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {displayText} <ExternalLink fontSize="inherit" />
              </Link>
            </Typography>
            <Tooltip title="Edit website">
              <IconButton size="small" onClick={startEdit} sx={{ p: 0.25 }}>
                <Pencil fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          {updatedAt && (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              Updated <FormattedDate value={updatedAt} />
            </Typography>
          )}
        </Box>
      ) : (
        <Box>
          <Button size="small" startIcon={<Plus fontSize="small" />} onClick={startEdit} sx={{ mt: 0.25 }}>
            Add website
          </Button>
        </Box>
      )}
    </Box>
  );
}
